import { useCallback } from 'react';
import { create } from 'zustand';

// ---------------------------------------------------------------------------------------------
// The interface's language (URS UX-02, docs §259). English is built in; a local language is a
// pack an administrator adds — a JSON object from each English text to its translation — and
// whatever a pack leaves out reads in English. The English text is the key, so wrapping a screen
// is t('Keep offline'), and the template an administrator fills in is the catalogue (strings.ts)
// with the menu's labels.
// ---------------------------------------------------------------------------------------------

export type Messages = Record<string, string>;

const STORAGE_KEY = 'annova.language';

function storedCode(): string {
  try { return localStorage.getItem(STORAGE_KEY) ?? 'en'; } catch { return 'en'; }
}

interface LanguageState {
  code: string;
  messages: Messages | null;
  /** Switches the interface to a language; English when the pack is null. */
  setLanguage: (code: string, messages: Messages | null) => void;
}

export const useLanguageStore = create<LanguageState>((set) => ({
  code: storedCode(),
  messages: null,
  setLanguage: (code, messages) => {
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* storage unavailable */ }
    document.documentElement.lang = code;
    set({ code, messages: code === 'en' ? null : messages });
  },
}));

/** The text in the current language, with {name} placeholders filled in. */
export function translate(messages: Messages | null, text: string, vars?: Record<string, string | number>): string {
  const found = messages?.[text];
  const out = found && found.trim() ? found : text;
  return vars ? out.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m)) : out;
}

/** The translator for the current language. */
export function useT() {
  const messages = useLanguageStore((s) => s.messages);
  return useCallback((text: string, vars?: Record<string, string | number>) => translate(messages, text, vars), [messages]);
}

/** Checks a pack an administrator uploads: an object of English texts to translations, every value text. */
export function parsePack(json: string): Messages {
  const value: unknown = JSON.parse(json);
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('A language pack is a JSON object from each English text to its translation.');
  const out: Messages = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v !== 'string') throw new Error(`"${k}" must be translated as text.`);
    if (v.trim()) out[k] = v;
  }
  return out;
}
